import { endOfDay, isSameDay } from 'date-fns';

interface NormalizeEventTimeToDayProps {
  startDate?: string;
  endDate?: string;
}

/**
 * Adjusts multi-day event times to fit within a single day.
 *
 * When an event spans multiple days, this function adjusts both the start and end times
 * to fit within the day of the start date, while preserving the original duration.
 *
 * @param normalizeEventTimeToDayProps - Configuration object containing event dates
 * @param normalizeEventTimeToDayProps.startDate - ISO string representing the event start date/time
 * @param normalizeEventTimeToDayProps.endDate - ISO string representing the event end date/time
 *
 * @returns Object containing the adjusted start and end dates
 * @returns adjustedStartDate - The adjusted start date (moved forward if needed to fit in one day)
 * @returns adjustedEndDate - The adjusted end date (set to end of start date's day, or unchanged if same day)
 *
 * @example
 * const result = normalizeEventTimeToDay({
 *   startDate: '2024-01-15T10:00:00Z',
 *   endDate: '2024-01-17T14:00:00Z' // 2 days + 4 hours duration
 * });
 * // Returns dates adjusted to fit within Jan 15, preserving the ~52 hour duration
 */
export function normalizeEventTimeToDay(normalizeEventTimeToDayProps: NormalizeEventTimeToDayProps) {
  const { startDate, endDate } = normalizeEventTimeToDayProps;

  let adjustedStartDate = startDate;
  let adjustedEndDate = endDate;

  if (startDate && endDate && !isSameDay(new Date(startDate), new Date(endDate))) {
    const duration = new Date(endDate).getTime() - new Date(startDate).getTime();
    const newEndDate = endOfDay(new Date(startDate));
    const newStartDate = new Date(newEndDate.getTime() - duration);

    adjustedStartDate = newStartDate.toISOString();
    adjustedEndDate = newEndDate.toISOString();
  }

  return { adjustedStartDate, adjustedEndDate };
}
